import type * as THREE from 'three';
import { MISSION } from '../config/tuning';
import type { Checksum, Checksummable } from '../core/Checksum';
import type { Mission, MissionObjective, ObjectiveState } from './schema';

/**
 * Objective tracking (SPEC §3.10.1).
 *
 * One state machine per objective, stepped at 10 Hz alongside the triggers. Objectives are the
 * mission's win condition, so the rule that matters is this: a *primary* objective failing fails
 * the mission, a secondary one merely costs score and the expert rank. That split is what lets a
 * mission ask for something hard without making it mandatory.
 *
 * Distances in metres, times in seconds. Nothing allocates per evaluation.
 */

export interface ObjectiveStatus {
  readonly id: string;
  readonly kind: MissionObjective['kind'];
  /**
   * What the objective is about: a waypoint id for `reach`, a target id for `destroy`.
   *
   * Carried on the status so callers outside the tracker can answer "which waypoint am I heading
   * for" without a second pass over the mission data — the world marker for the next checkpoint is
   * the first such caller.
   */
  readonly targetRef: string | undefined;
  readonly primary: boolean;
  readonly description: string;
  readonly weight: number;
  state: ObjectiveState;
  /** 0–1. `destroy` uses the impact effect; `survive` uses elapsed fraction. */
  progress: number;
}

/** What the objective system needs to know each evaluation. Supplied by the mission runner. */
export interface ObjectiveWorld {
  readonly positionM: THREE.Vector3;
  readonly elapsedS: number;
  readonly integrity: number;
  /** Destroyed target ids this run. */
  readonly destroyed: ReadonlySet<string>;
  /** Waypoint id → world position. */
  readonly waypoints: ReadonlyMap<string, THREE.Vector3>;
  /** Ids the player was told to leave alone but struck anyway. */
  readonly struck: ReadonlySet<string>;
  /** Target ids currently held steady in the camera frustum, for `photograph`. */
  readonly photographed: ReadonlySet<string>;
}

export class ObjectiveTracker implements Checksummable {
  readonly statuses: ObjectiveStatus[] = [];

  private readonly objectives: readonly MissionObjective[];
  private readonly reachedAtS = new Map<string, number>();

  constructor(mission: Mission) {
    this.objectives = mission.objectives;
    for (let i = 0; i < this.objectives.length; i += 1) {
      const objective = this.objectives[i];
      this.statuses.push({
        id: objective.id,
        kind: objective.kind,
        targetRef: objective.targetRef,
        primary: objective.primary,
        description: objective.description,
        weight: objective.weight,
        state: 'pending',
        progress: 0,
      });
    }
  }

  /** Force a state, for the `setObjective` mission action. */
  set(id: string, state: ObjectiveState): void {
    const status = this.find(id);
    if (status === null) return;
    status.state = state;
    if (state === 'complete') status.progress = 1;
  }

  find(id: string): ObjectiveStatus | null {
    for (let i = 0; i < this.statuses.length; i += 1) {
      if (this.statuses[i].id === id) return this.statuses[i];
    }
    return null;
  }

  /** Step every pending objective. Called at `MISSION.evaluateHz`. */
  update(world: ObjectiveWorld): void {
    for (let i = 0; i < this.objectives.length; i += 1) {
      const objective = this.objectives[i];
      const status = this.statuses[i];
      if (status.state !== 'pending') continue;
      this.evaluate(objective, status, world);
    }
  }

  private evaluate(
    objective: MissionObjective,
    status: ObjectiveStatus,
    world: ObjectiveWorld,
  ): void {
    switch (objective.kind) {
      case 'destroy': {
        if (objective.targetRef !== undefined && world.destroyed.has(objective.targetRef)) {
          status.state = 'complete';
          status.progress = 1;
        }
        break;
      }
      case 'reach': {
        const ref = objective.targetRef;
        if (ref === undefined) break;
        const waypoint = world.waypoints.get(ref);
        if (waypoint === undefined) break;
        const radiusM = objective.radiusM ?? MISSION.waypointRadiusM;
        const distanceM = world.positionM.distanceTo(waypoint);
        status.progress = Math.max(status.progress, clamp01(1 - distanceM / (radiusM * 8)));
        if (distanceM <= radiusM) {
          status.state = 'complete';
          status.progress = 1;
        }
        break;
      }
      case 'survive': {
        const seconds = objective.seconds ?? 0;
        // "Survive" is about the airframe, not the clock alone: being destroyed fails it
        // immediately rather than waiting for a timer that will never be beaten.
        if (world.integrity <= 0) {
          status.state = 'failed';
          break;
        }
        status.progress = seconds <= 0 ? 1 : clamp01(world.elapsedS / seconds);
        if (world.elapsedS >= seconds) {
          status.state = 'complete';
          status.progress = 1;
        }
        break;
      }
      case 'avoid': {
        // Stays pending until the mission ends — an "avoid" is only satisfied by never having
        // done the thing, so completion is decided in `finalise`.
        if (objective.targetRef !== undefined && world.struck.has(objective.targetRef)) {
          status.state = 'failed';
        }
        break;
      }
      case 'photograph': {
        const ref = objective.targetRef;
        if (ref === undefined) break;
        if (world.photographed.has(ref)) {
          const since = this.reachedAtS.get(ref);
          if (since === undefined) {
            this.reachedAtS.set(ref, world.elapsedS);
          } else {
            const held = world.elapsedS - since;
            status.progress = clamp01(held / MISSION.photographHoldS);
            if (held >= MISSION.photographHoldS) {
              status.state = 'complete';
              status.progress = 1;
            }
          }
        } else {
          // Lost the frame: the hold restarts. A photograph is a steady shot, not a glimpse.
          this.reachedAtS.delete(ref);
          status.progress = 0;
        }
        break;
      }
      default:
        break;
    }
  }

  /**
   * Resolve everything still pending once the mission ends.
   *
   * `avoid` completes here precisely because it is the absence of an event: it cannot be confirmed
   * until there is no more time in which to violate it.
   */
  finalise(): void {
    for (let i = 0; i < this.statuses.length; i += 1) {
      const status = this.statuses[i];
      if (status.state !== 'pending') continue;
      if (status.kind === 'avoid') {
        status.state = 'complete';
        status.progress = 1;
      } else {
        status.state = 'failed';
      }
    }
  }

  get allPrimaryComplete(): boolean {
    for (let i = 0; i < this.statuses.length; i += 1) {
      const status = this.statuses[i];
      if (status.primary && status.state !== 'complete') return false;
    }
    return true;
  }

  get anyPrimaryFailed(): boolean {
    for (let i = 0; i < this.statuses.length; i += 1) {
      const status = this.statuses[i];
      if (status.primary && status.state === 'failed') return true;
    }
    return false;
  }

  get allComplete(): boolean {
    for (let i = 0; i < this.statuses.length; i += 1) {
      if (this.statuses[i].state !== 'complete') return false;
    }
    return true;
  }

  checksum(into: Checksum): void {
    for (let i = 0; i < this.statuses.length; i += 1) {
      const status = this.statuses[i];
      into.addInt(status.state === 'complete' ? 2 : status.state === 'failed' ? 1 : 0);
      into.addFloat(status.progress);
    }
  }
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}
